import { useMutation } from "@tanstack/react-query";
import api, { apiFormData } from "../../utils/api";
import { useInvalidateDomain } from "../../lib/invalidation";
import { normalizeShipment } from "./queries";
import { appendShipmentEvent } from "./types";
import type {
  Shipment,
  ShipmentEventInput,
  ShipmentMerchandise,
  UpdateShipmentData,
} from "./types";

export function useUpdateShipment() {
  const invalidateDomain = useInvalidateDomain();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UpdateShipmentData;
    }): Promise<Shipment> =>
      api
        .put("/shipment", data, { params: { id } })
        .then((res) => normalizeShipment(res.data?.data?.data ?? res.data?.data)),
    onSuccess: () => invalidateDomain("shipments"),
  });
}

export function useAddShipmentEvent() {
  const invalidateDomain = useInvalidateDomain();
  return useMutation({
    mutationFn: ({
      shipment,
      event,
      files = [],
    }: {
      shipment: Shipment;
      event: ShipmentEventInput;
      files?: File[];
    }): Promise<Shipment> => {
      const updatedEventHistory = appendShipmentEvent(shipment, event);

      if (files && files.length > 0) {
        const formData = new FormData();
        formData.append("event_history", JSON.stringify(updatedEventHistory));
        files.forEach((file) => {
          formData.append("files", file);
        });

        console.log("📤 Sending FormData with", files.length, "files:", files.map(f => f.name));

        return apiFormData
          .put("/shipment", formData, {
            params: { id: shipment.id },
          })
          .then((res) => {
            console.log("✅ Response:", res.data);
            return normalizeShipment(res.data?.data?.data ?? res.data?.data);
          });
      }

      return api
        .put(
          "/shipment",
          { event_history: updatedEventHistory },
          { params: { id: shipment.id } },
        )
        .then((res) => normalizeShipment(res.data?.data?.data ?? res.data?.data));
    },
    onSuccess: () => invalidateDomain("shipments"),
  });
}

export function useDeleteShipment() {
  const invalidateDomain = useInvalidateDomain();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete("/shipment", { params: { id } }).then((res) => res.data),
    onSuccess: () => invalidateDomain("shipments"),
  });
}

export function useCreateShipment() {
  const invalidateDomain = useInvalidateDomain();
  return useMutation({
    mutationFn: (data: {
      company_id: string;
      client_info: {
        client_name: string;
        client_email: string;
        client_phone: string;
      };
      shipment_name: string;
      origin: { lat: number; long: number; address: string };
      destination: { lat: number; long: number; address: string };
      tracking_number: string;
      transportation_type: string;
      /** Expediente documental (CC-CONSOLE #1632): deciden la plantilla de documentos. */
      direction?: "importacion" | "exportacion" | "nacional";
      incoterm?: string;
      status_id: string;
      smart_tracking: string;
      actual_device_id?: string;
      trip_id?: string;
      note?: string;
      event_history?: any[];
      contacts?: Array<{ id: string }> | null;
      notification_frequency?: string | null;
      merchandise?: ShipmentMerchandise[];
      /** Guía de paquetería (17TRACK): la API la registra tras crear el envío. */
      carrier_tracking_number?: string;
      carrier_code?: number | null;
    }): Promise<Shipment & { carrier_tracking_error?: string }> => {
      console.log("Creating shipment with data:", data);
      const requestData = {
        company_id: data.company_id,
        client_info: data.client_info,
        shipment_name: data.shipment_name,
        origin: data.origin,
        destination: data.destination,
        tracking_number: data.tracking_number,
        transportation_type: data.transportation_type,
        status_id: data.status_id,
        smart_tracking: data.smart_tracking,
        ...(data.actual_device_id && { actual_device_id: data.actual_device_id }),
        ...(data.trip_id && { trip_id: data.trip_id }),
        ...(data.note !== undefined && { note: data.note }),
        ...(data.event_history && { event_history: data.event_history }),
        ...(data.contacts !== undefined && { contacts: data.contacts }),
        ...(data.notification_frequency !== undefined && { notification_frequency: data.notification_frequency }),
        ...(data.merchandise !== undefined && { merchandise: data.merchandise }),
        ...(data.carrier_tracking_number && { carrier_tracking_number: data.carrier_tracking_number }),
        ...(data.carrier_tracking_number && data.carrier_code && { carrier_code: data.carrier_code }),
        ...(data.direction && { direction: data.direction }),
        ...(data.incoterm && { incoterm: data.incoterm }),
      };

      console.log("Request payload:", JSON.stringify(requestData, null, 2));

      return api
        .post("/shipment", requestData)
        .then((res) => {
          console.log("Create response:", res.data);
          const raw = res.data?.data ?? res.data;
          const carrierError =
            typeof raw?.carrier_tracking_error === "string" ? raw.carrier_tracking_error : undefined;
          return {
            ...normalizeShipment(raw),
            ...(carrierError ? { carrier_tracking_error: carrierError } : {}),
          };
        });
    },
    onSuccess: () => invalidateDomain("shipments"),
  });
}
